// The link/session resume flow: a "Reconnecting…" modal over the shared
// `resumeRoom` helper, and the failure modal when the seat cannot be had.
// Takes only the collaborators it needs, never the host class itself.
import {
  resumeRoom as resumeRoomFlow,
  type ResumeFailureCause,
  type SessionStore,
} from '@bn-games/room-client';
import { fetchMe, isLoggedIn } from '@bn-games/auth-client';
import { openModal, openResumeFailureModal } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import type { ChessRoom } from './OnlineMode.js';

export interface OnlineResumeDeps {
  readonly room: ChessRoom;
  readonly sessionStore: SessionStore;
  readonly playerName: () => string;
  /** The seat is gone for good: forget what was saved for this room. */
  readonly onSeatLost: () => void;
}

/**
 * Resume a held seat by room code — the entry point for an invite or share
 * link's `#room=CODE`, a reload mid-game, and the "Resume online" menu button.
 * The server decides whether the code reclaims our seat or seats us as the
 * second player; either way the table mounts itself once the room is ready.
 */
export function performRoomResume(deps: OnlineResumeDeps, roomCode: string, legacyToken?: string): Promise<void> {
  // An old `#room=CODE&token=…` link: offered to the server as one more
  // candidate token, never trusted as an identity on its own.
  if (legacyToken !== undefined) deps.sessionStore.saveFor(roomCode, legacyToken);
  const modal = openModal({
    title: t('online.resumeModal.reconnectingTitle'),
    body: t('online.resumeModal.reconnectingBody'),
    actions: [],
    dismissible: false,
  });
  // `RoomClient` keys the store by the upper-cased code.
  const held = deps.sessionStore.loadFor(roomCode.toUpperCase());
  return resumeRoomFlow(deps.room, { roomCode, playerName: deps.playerName() }).then((outcome) => {
    modal.close();
    if (outcome.kind !== 'failed') return;
    // An unreachable server says nothing about the seat, but the shared flow
    // clears the stored token on every failure. Put it back, or a reload
    // while offline would cost an anonymous player the game.
    if (outcome.cause === 'UNREACHABLE' && held !== null) deps.sessionStore.saveFor(held.roomCode, held.sessionToken);
    showResumeFailure(deps, outcome.cause, roomCode);
  });
}

function showResumeFailure(deps: OnlineResumeDeps, cause: ResumeFailureCause, roomCode: string): void {
  if (cause !== 'UNREACHABLE') deps.onSeatLost();
  openResumeFailureModal({
    cause,
    signedIn: isLoggedIn(),
    onRetry: () => {
      void performRoomResume(deps, roomCode);
    },
    // Signing in does not reclaim this seat, but the next attempt should see it.
    onSignIn: () => {
      void fetchMe();
    },
  });
}
